<template>
  <div class="billing-container">
    <!-- Top bar: Select Labor + Action Buttons -->
    <div class="billing-topbar">
      <div class="labor-select-wrap">
        <label class="select-label">Add Labor</label>
        <el-select
          v-model="selectedLabor"
          clearable
          filterable
          size="small"
          style="width: 280px"
          value-key="laborCostId"
          placeholder="Search and select…"
          @change="addServiceRequestLabors"
        >
          <el-option
            v-for="(item, index) in laborCosts"
            :key="index"
            :value="item"
            :label="`${item.description} (₹${item.cost})`"
          />
        </el-select>
      </div>
      <div class="billing-action-buttons">
        <base-button
          v-if="hasChalans"
          :loading="generatingBillRequest"
          :disabled="generatingBillRequest"
          size="sm"
          type="primary"
          @click="createBillRequest()"
          >Bill Request</base-button
        >
        <base-button
          v-if="serviceRequest.vendorInvoice"
          type="success"
          size="sm"
          @click="getVendorInvoiceProducts()"
          >✓ Vendor Invoice</base-button
        >
        <base-button
          v-else
          size="sm"
          type="warning"
          @click="getVendorInvoiceProducts()"
          >Gen. Vendor Invoice</base-button
        >
        <base-button
          v-if="serviceRequest.saleId"
          type="success"
          size="sm"
          @click="getSaleInvoiceProducts()"
          >✓ Sale Invoice</base-button
        >
        <base-button
          v-else
          size="sm"
          type="primary"
          @click="getSaleInvoiceProducts()"
          >Gen. Sale Invoice</base-button
        >
      </div>
    </div>

    <!-- Labor Table -->
    <div class="overflow-x-auto mt-3">
      <table class="labor-table">
        <thead>
          <tr>
            <th class="col-desc">Description</th>
            <th class="col-cost">Cost (₹)</th>
            <th class="col-qty">Qty</th>
            <th class="col-disc">Disc</th>
            <th class="col-total">Total</th>
            <th class="col-action">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="serviceRequestLabors.length === 0">
            <td colspan="6" class="empty-row">No labor entries yet.</td>
          </tr>
          <tr
            v-for="row in serviceRequestLabors"
            :key="row.serviceRequestLaborId"
          >
            <td class="col-desc" :title="row.description">
              {{ row.description }}
            </td>
            <td class="col-cost">
              <div v-if="row.isLaborEditable" class="cost-edit-wrap">
                <input
                  v-model="row.laborCost"
                  type="number"
                  class="cost-input"
                />
                <button class="btn-save" @click="updateLaborCost(row)">Save</button>
              </div>
              <div v-else class="cost-view-wrap">
                <span>{{ row.laborCost }}</span>
                <button class="btn-edit" @click="row.isLaborEditable = true">Edit</button>
              </div>
            </td>
            <td class="col-qty">
              <div class="qty-wrap">
                <button class="qty-btn" @click="decrementQty(row)">−</button>
                <input
                  v-model.number="row.quantity"
                  type="number"
                  min="1"
                  class="qty-input"
                  @change="updateQuantity(row)"
                />
                <button class="qty-btn" @click="incrementQty(row)">+</button>
              </div>
            </td>
            <td class="col-disc">{{ row.discount || 0 }}</td>
            <td class="col-total">{{ calculateTotal(row) }}</td>
            <td class="col-action">
              <button class="btn-delete" @click="deleteLabor(row)">Delete</button>
            </td>
          </tr>
        </tbody>
        <tfoot v-if="serviceRequestLabors.length > 0">
          <tr>
            <td colspan="4" class="total-label">Labor Total</td>
            <td class="total-value">{{ vendorLaborTotal }}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>
    </div>

    <!-- Assistant / Helper Labor Share -->
    <div v-if="assistants.length > 0" class="assistant-section">
      <div class="assistant-header">
        <div>
          <span class="assistant-title">Helper / Assistant Labor Share</span>
          <span class="vendor-labor-hint">
            — Vendor Labor Total: <strong>₹{{ vendorLaborTotal }}</strong>
          </span>
        </div>
        <base-button
          size="sm"
          type="primary"
          :loading="savingAssistantLabor"
          :disabled="savingAssistantLabor"
          @click="saveAssistantLabor()"
          >Update Share</base-button
        >
      </div>
      <div class="assistant-rows">
        <div
          v-for="a in assistants"
          :key="a.serviceRequestAssistantId"
          class="assistant-row"
        >
          <span class="assistant-name">{{ a.name }}</span>
          <span class="assistant-badge">{{ a.type }}</span>
          <div class="assistant-input-wrap">
            <span class="rupee-sign">₹</span>
            <input
              v-model="a.labor"
              type="number"
              min="0"
              class="assistant-amount-input"
              placeholder="0"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Vendor Invoice Dialog -->
    <el-dialog :visible.sync="showVendorInvoiceData" title="Vendor Invoice">
      <el-table
        :data="vendorInvoiceData.labors"
        :show-summary="true"
        :summary-method="getTotalOfVendorInvoice"
        height="350"
      >
        <el-table-column label="Name">
          <template slot-scope="scope">
            <div>{{ scope.row.description }}</div>
            <div>Qty: {{ scope.row.quantity }}</div>
            <div>Disc: {{ scope.row.discount }}</div>
          </template>
        </el-table-column>
        <el-table-column label="Price">
          <template slot-scope="scope">
            {{ scope.row.laborCost | currency }}
          </template>
        </el-table-column>
        <el-table-column label="Total">
          <template slot-scope="scope">
            {{ scope.row.total | currency }}
          </template>
        </el-table-column>
        <template slot="append">
          <tr>
            <td>
              External Goods Total:
              {{ vendorInvoiceData.externalGoodsTotal | currency }}
            </td>
          </tr>
        </template>
      </el-table>
      <span slot="footer" class="dialog-footer">
        <el-button
          v-if="!serviceRequest.vendorInvoice"
          type="primary"
          @click="generateVendorsInvoice()"
        >
          Generate Vendor's Invoice
        </el-button>
      </span>
    </el-dialog>

    <!-- Sale Invoice Dialog -->
    <el-dialog
      :visible.sync="showSaleInvoiceProducts"
      title="Sale Invoice Products"
      width="80%"
    >
      <el-table
        :data="saleInvoiceProducts"
        :show-summary="true"
        :summary-method="getSummary"
        sum-text="Total"
        height="350"
      >
        <el-table-column label="Name">
          <template slot-scope="scope">{{ scope.row.name }}</template>
        </el-table-column>
        <el-table-column label="Qty">
          <template slot-scope="scope">{{ scope.row.quantity }}</template>
        </el-table-column>
        <el-table-column label="Price">
          <template slot-scope="scope">{{ scope.row.price }}</template>
        </el-table-column>
        <el-table-column label="Disc.">
          <template slot-scope="scope">
            <span v-if="scope.row.isPercentage">{{ scope.row.discount }}%</span>
            <span v-else>{{ scope.row.discount | currency }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Tax Rate">
          <template slot-scope="scope">{{ scope.row.taxRate }}%</template>
        </el-table-column>
        <el-table-column label="Total">
          <template slot-scope="scope">{{ scope.row.total | currency }}</template>
        </el-table-column>
      </el-table>
      <span slot="footer" class="dialog-footer">
        <el-button
          v-if="!serviceRequest.saleId"
          type="primary"
          @click="generateSale()"
        >
          Generate Invoice
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import {
  Select,
  Option,
  Table,
  TableColumn,
  Tag,
  Dialog,
  Button
} from "element-ui";
import endpoints from "@/utils/constants";
import { apiHelper } from "@/utils/helpers";

export default {
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Tag.name]: Tag,
    [Dialog.name]: Dialog,
    [Button.name]: Button
  },
  filters: {
    currency(value) {
      const amount = parseFloat(value) || 0;
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR"
      })
        .format(amount)
        .replace(".00", "");
    }
  },
  data: () => ({
    selectedLabor: {},
    vendorInvoiceData: {},
    showVendorInvoiceData: false,
    saleInvoiceProducts: [],
    showSaleInvoiceProducts: false,
    generatingBillRequest: false,
    assistants: [],
    savingAssistantLabor: false,
    assistantsFetched: false
  }),
  computed: {
    ...mapState("laborCost", ["laborCosts"]),
    ...mapState("serviceRequest", ["serviceRequest"]),
    ...mapState("serviceRequestChalan", [
      "serviceRequestChalan",
      "serviceRequestChalansList"
    ]),
    ...mapState("serviceRequestLabor", [
      "serviceRequestLabors",
      "serviceRequestLabor"
    ]),
    ...mapGetters({
      statusList: "status/getStatusList",
      serviceRequestTransactions:
        "serviceRequestTransaction/getServiceRequestTransactions"
    }),
    hasChalans() {
      return (
        this.serviceRequestChalansList &&
        this.serviceRequestChalansList.length > 0
      );
    },
    vendorLaborTotal() {
      return this.serviceRequestLabors.reduce(
        (sum, l) => sum + l.laborCost * l.quantity - (l.discount || 0),
        0
      );
    }
  },
  watch: {
    "serviceRequest.vendorId": {
      immediate: true,
      handler(id) {
        if (id && !this.assistantsFetched) {
          this.fetchAssistants();
        }
      }
    }
  },
  mounted() {
    this.getLaborCosts();
    this.getServiceRequestLabors(this.$route.params.id);
    this.getServiceRequestChalans(this.$route.params.id);
  },
  methods: {
    ...mapActions({
      getLaborCosts: "laborCost/getLaborCosts",
      getServiceRequestLabors: "serviceRequestLabor/getServiceRequestLabors",
      saveServiceRequestLabor: "serviceRequestLabor/saveServiceRequestLabor",
      updateLabor: "serviceRequestLabor/updateServiceRequestLabor",
      generateServiceRequestSale: "serviceRequest/generateSale",
      fetchServiceRequest: "serviceRequest/fetchServiceRequest",
      generateVendorsInvoiceNumber: "serviceRequest/generateVendorsInvoiceNumber",
      deleteServiceRequestLabor: "serviceRequestLabor/deleteServiceRequestLabor",
      getVendorInvoiceData: "serviceRequest/getVendorInvoiceData",
      fetchSaleInvoiceProducts: "serviceRequest/getSaleInvoiceProducts",
      generateBillRequest: "serviceRequest/generateBillRequest",
      getServiceRequestChalans: "serviceRequestChalan/getServiceRequestChalans"
    }),
    ...mapMutations({
      setServiceRequestLabor: "serviceRequestLabor/setServiceRequestLabor"
    }),

    addServiceRequestLabors(item) {
      if (this.serviceRequest.status.code === "completed") {
        this.$notify({
          message: "Cannot add labor on a completed service request.",
          type: "danger",
          title: "Failure"
        });
        return;
      }
      if (!item || !item.cost) {
        this.$notify({ message: "Cost Not Added!", type: "danger", title: "Failure" });
        return;
      }
      this.setServiceRequestLabor({
        quantity: 1,
        laborCost: item.cost,
        serviceRequestId: this.$route.params.id,
        description: item.description,
        vendorId: this.serviceRequest.vendorId
      });
      this.saveServiceRequestLabor()
        .then(res => {
          if (res.status === 201) {
            this.selectedLabor = {};
            this.$notify({ message: "Labor Added!", type: "success", title: "Success" });
          }
          this.getServiceRequestLabors(this.$route.params.id);
        })
        .catch(() => {
          this.$notify({ message: "Try Again!", type: "danger", title: "Failure" });
        });
    },

    incrementQty(row) {
      row.quantity = (row.quantity || 1) + 1;
      this.updateQuantity(row);
    },
    decrementQty(row) {
      if (row.quantity > 1) {
        row.quantity = row.quantity - 1;
        this.updateQuantity(row);
      }
    },
    async updateQuantity(row) {
      await this.updateLabor({
        quantity: row.quantity,
        serviceRequestId: this.$route.params.id,
        serviceRequestLaborId: row.serviceRequestLaborId
      });
      this.$notify({ message: "Quantity Updated!", type: "success", title: "Success" });
    },
    updateLaborCost(row) {
      this.updateLabor({
        laborCost: row.laborCost,
        serviceRequestId: this.$route.params.id,
        serviceRequestLaborId: row.serviceRequestLaborId
      }).then(() => {
        row.isLaborEditable = false;
        this.getServiceRequestLabors(this.$route.params.id);
        this.$notify({ message: "Labor Updated!", type: "success", title: "Success" });
      });
    },
    calculateTotal(row) {
      return row.laborCost * row.quantity - (row.discount || 0);
    },
    deleteLabor(row) {
      this.deleteServiceRequestLabor({
        serviceRequestId: row.serviceRequestId,
        serviceRequestLaborId: row.serviceRequestLaborId
      }).then(res => {
        if (res.status === 201) {
          this.getServiceRequestLabors(this.$route.params.id);
          this.$notify({ message: "Labor Deleted!", type: "success", title: "Success" });
        }
      });
    },

    async fetchAssistants() {
      const vendorId = this.serviceRequest && this.serviceRequest.vendorId;
      if (!vendorId) return;
      this.assistantsFetched = true;
      const srId = this.$route.params.id;
      try {
        const res = await apiHelper.call(
          "get",
          `${endpoints.LARAVEL_BASE_URL}/api/v1/vendors/${vendorId}/service-requests/${srId}/assistants`
        );
        const rows = Array.isArray(res.data) ? res.data : [];
        this.assistants = rows
          .filter(r => (r.vendor_id || r.vendorId || "").trim())
          .map(r => ({
            serviceRequestAssistantId: (
              r.service_request_assistant_id ||
              r.serviceRequestAssistantId ||
              r.id ||
              ""
            ).toString().trim(),
            vendorId: (r.vendor_id || r.vendorId || "").toString().trim(),
            name: (
              r.name || r.vendor_name || r.vendorName ||
              r.assistant_name || r.assistantName || "—"
            ).toString().trim(),
            type: (r.type || r.assistant_type || r.assistantType || "helper")
              .toString().trim().toLowerCase(),
            labor: String(r.labor || r.assistant_labor || r.assistantLabor || "0")
          }));
      } catch (_) {
        // No assistants or endpoint unavailable
      }
    },

    async saveAssistantLabor() {
      const vendorId = this.serviceRequest && this.serviceRequest.vendorId;
      if (!vendorId) return;
      this.savingAssistantLabor = true;
      const payload = {
        assistants: this.assistants
          .filter(a => a.serviceRequestAssistantId && a.vendorId)
          .map(a => ({
            service_request_assistant_id: a.serviceRequestAssistantId,
            vendor_id: a.vendorId,
            assistant_type: a.type || "helper",
            labor: String(a.labor || "0")
          }))
      };
      try {
        await apiHelper.call(
          "post",
          `${endpoints.LARAVEL_BASE_URL}/api/v1/vendors/${vendorId}/service-requests/${this.$route.params.id}/assistants/labor`,
          payload
        );
        this.$notify({ message: "Assistant labor share updated!", type: "success", title: "Success" });
      } catch (e) {
        const msg =
          (e.response && e.response.data && e.response.data.message) ||
          "Failed to update. Please try again.";
        this.$notify({ message: msg, type: "danger", title: "Error" });
      } finally {
        this.savingAssistantLabor = false;
      }
    },

    async getSaleInvoiceProducts() {
      const pendingChalan =
        this.serviceRequestChalansList &&
        this.serviceRequestChalansList.find(
          i => i.status.label === "Pending" || i.status.label === "Dispatched"
        );
      if (!this.serviceRequestChalansList || !this.serviceRequestChalansList.length) {
        this.$notify({ message: "No Chalan Added!", type: "warning", title: "Warning" });
      } else if (pendingChalan) {
        this.$notify({
          message: "Chalans are Pending or Dispatched — sale can't be generated.",
          type: "warning",
          title: "Warning"
        });
      } else {
        this.saleInvoiceProducts = await this.fetchSaleInvoiceProducts(
          this.serviceRequest.serviceRequestId
        );
        this.showSaleInvoiceProducts = true;
      }
    },
    getSummary(param) {
      const { columns, data } = param;
      const sums = [];
      columns.forEach((col, idx) => {
        if (idx === 0) { sums[idx] = "Total"; return; }
        if (idx === 5) {
          sums[idx] = "Rs. " + data.reduce((a, b) => a + b.total, 0);
        }
      });
      return sums;
    },
    generateSale() {
      this.generateServiceRequestSale(this.$route.params.id)
        .then(() => {
          this.$notify({ message: "Sale Generated!", type: "success", title: "Success" });
          this.showSaleInvoiceProducts = false;
          this.fetchServiceRequest(this.$route.params.id);
        })
        .catch(() => {
          this.$notify({ message: "Error generating sale. Try again.", type: "danger", title: "Error" });
        });
    },
    createBillRequest() {
      if (!this.hasChalans || this.generatingBillRequest) return;
      this.generatingBillRequest = true;
      this.generateBillRequest(this.$route.params.id)
        .then(() => {
          this.$notify({ message: "Bill request created!", type: "success", title: "Success" });
        })
        .catch(err => {
          this.$notify({
            message:
              (err.response && err.response.data && err.response.data.message) ||
              "Error creating bill request.",
            type: "danger",
            title: "Error"
          });
        })
        .finally(() => { this.generatingBillRequest = false; });
    },
    async getVendorInvoiceProducts() {
      this.vendorInvoiceData = await this.getVendorInvoiceData(
        this.serviceRequest.serviceRequestId
      );
      this.showVendorInvoiceData = true;
    },
    getTotalOfVendorInvoice(param) {
      const { columns, data } = param;
      const sums = [];
      columns.forEach((col, idx) => {
        if (idx === 0) { sums[idx] = "Total"; return; }
        if (idx === 2) {
          let sum = data.reduce((a, b) => a + b.total, 0);
          sum += this.vendorInvoiceData.externalGoodsTotal || 0;
          sums[idx] = "Rs. " + sum;
        }
      });
      return sums;
    },
    generateVendorsInvoice() {
      if (
        this.serviceRequest.externalGoodMeta != {} ||
        this.serviceRequestLabors.length > 0
      ) {
        this.generateVendorsInvoiceNumber().then(res => {
          if (res) {
            this.$notify({ message: "Vendor Invoice Generated!", type: "success", title: "Success" });
            this.showVendorInvoiceData = false;
          }
        });
      } else {
        this.$notify({
          message: "No External Good/s or Labor Added.",
          type: "warning",
          title: "Warning"
        });
      }
    }
  }
};
</script>

<style scoped>
.billing-container {
  padding: 0 4px 80px;
}

/* Top bar */
.billing-topbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 4px;
}
.labor-select-wrap {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.select-label {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  margin: 0;
}
.billing-action-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

/* Labor table */
.labor-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  min-width: 600px;
}
.labor-table thead tr {
  background: #f3f4f6;
}
.labor-table th {
  padding: 8px 10px;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  text-align: left;
  border-bottom: 2px solid #e5e7eb;
  white-space: nowrap;
}
.labor-table td {
  padding: 8px 10px;
  border-bottom: 1px solid #f3f4f6;
  color: #111827;
  vertical-align: middle;
}
.labor-table tbody tr:hover {
  background: #f9fafb;
}
.labor-table tfoot td {
  padding: 8px 10px;
  border-top: 2px solid #e5e7eb;
  font-weight: 600;
}

/* Column widths */
.col-desc { width: 200px; max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.col-cost { width: 150px; }
.col-qty  { width: 120px; }
.col-disc { width: 60px; white-space: nowrap; }
.col-total{ width: 80px; white-space: nowrap; font-weight: 500; }
.col-action { width: 80px; }
.total-label { text-align: right; color: #6b7280; font-size: 12px; }
.total-value { color: #111827; font-weight: 700; }

.empty-row {
  text-align: center;
  color: #9ca3af;
  padding: 24px 0;
  font-size: 13px;
}

/* Cost edit */
.cost-edit-wrap, .cost-view-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}
.cost-input {
  width: 70px;
  height: 28px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 0 6px;
  font-size: 13px;
  outline: none;
}
.cost-input:focus { border-color: #3b82f6; }
.btn-edit {
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 4px;
  border: 1px solid #6366f1;
  background: #6366f1;
  color: #fff;
  cursor: pointer;
}
.btn-save {
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 4px;
  border: 1px solid #10b981;
  background: #10b981;
  color: #fff;
  cursor: pointer;
}

/* Qty controls */
.qty-wrap {
  display: flex;
  align-items: center;
  gap: 0;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  overflow: hidden;
  width: fit-content;
}
.qty-btn {
  width: 28px;
  height: 28px;
  border: none;
  background: #f3f4f6;
  font-size: 16px;
  cursor: pointer;
  color: #374151;
  line-height: 1;
}
.qty-btn:hover { background: #e5e7eb; }
.qty-input {
  width: 44px;
  height: 28px;
  border: none;
  border-left: 1px solid #d1d5db;
  border-right: 1px solid #d1d5db;
  text-align: center;
  font-size: 13px;
  outline: none;
  -moz-appearance: textfield;
}
.qty-input::-webkit-inner-spin-button,
.qty-input::-webkit-outer-spin-button { -webkit-appearance: none; }

.btn-delete {
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 4px;
  border: 1px solid #ef4444;
  background: #ef4444;
  color: #fff;
  cursor: pointer;
  white-space: nowrap;
}
.btn-delete:hover { background: #dc2626; }

/* Assistant section */
.assistant-section {
  margin-top: 20px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}
.assistant-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #f0fdf4;
  border-bottom: 1px solid #d1fae5;
}
.assistant-title {
  font-size: 13px;
  font-weight: 600;
  color: #065f46;
}
.vendor-labor-hint {
  font-size: 12px;
  color: #6b7280;
  margin-left: 6px;
}
.assistant-rows {
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.assistant-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}
.assistant-name {
  flex: 1;
  font-size: 13px;
  font-weight: 500;
  color: #111827;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.assistant-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 999px;
  background: #dbeafe;
  color: #1d4ed8;
  text-transform: capitalize;
  white-space: nowrap;
}
.assistant-input-wrap {
  display: flex;
  align-items: center;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  overflow: hidden;
  background: #fff;
}
.rupee-sign {
  padding: 0 6px;
  font-size: 12px;
  color: #6b7280;
  background: #f3f4f6;
  border-right: 1px solid #d1d5db;
  line-height: 28px;
  height: 28px;
}
.assistant-amount-input {
  width: 80px;
  height: 28px;
  border: none;
  outline: none;
  padding: 0 8px;
  font-size: 13px;
  color: #111827;
  -moz-appearance: textfield;
}
.assistant-amount-input::-webkit-inner-spin-button,
.assistant-amount-input::-webkit-outer-spin-button { -webkit-appearance: none; }
</style>
